import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Center,
  Stepper,
  Alert,
  Image,
  Notification,
  FileButton
} from '@mantine/core';
import { IconArrowLeft, IconId, IconCamera, IconCheck, IconQrcode } from '@tabler/icons-react';
import QRCode from 'qrcode';

const IdentityVerificationPage = () => {
  const [step, setStep] = useState(0);
  const [qrCode, setQrCode] = useState('');
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [idCaptured, setIdCaptured] = useState(false);
  const [selfieCaptured, setSelfieCaptured] = useState(false);
  const [ageVerified, setAgeVerified] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [humanVerified, setHumanVerified] = useState(false);
  const [frontIdPreview, setFrontIdPreview] = useState(null);
  const [backIdPreview, setBackIdPreview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const generateQR = async () => {
      const verificationData = JSON.stringify({
        userId: 'pending',
        action: 'verify-identity',
        timestamp: Date.now()
      });
      try {
        const qr = await QRCode.toDataURL(verificationData, { width: 200, margin: 2 });
        setQrCode(qr);
      } catch (err) {
        console.error('QR generation failed:', err);
      }
    };
    if (step === 0) {
      generateQR();
    }
  }, [step]);

  const handleQRScanned = () => {
    setStep(1);
  };

  const handleHumanVerification = () => {
    setHumanVerified(true);
    setStep(1);
  };

  const handleFrontIdUpload = (file) => {
    if (file) {
      setFrontIdPreview(URL.createObjectURL(file));
      setIdCaptured(true);
      setStep(2);
    }
  };

  const handleBackIdUpload = (file) => {
    if (file) {
      setBackIdPreview(URL.createObjectURL(file));
      setIdCaptured(true);
      setTimeout(() => setAgeVerified(true), 1500);
      setStep(3);
    }
  };

  const handleSelfieCapture = () => {
    setSelfieCaptured(true);
    setStep(4);
    setVerificationComplete(true);
  };

  const handleClose = () => {
    navigate('/build-profile');
  };

  const renderStepper = () => (
    <Stepper active={isMobile && humanVerified ? step - 1 : step} orientation="vertical">
      {!isMobile && (
        <Stepper.Step
          label="Scan QR Code"
          description="Scan to start verification"
          icon={<IconQrcode size={18} />}
        >
          <Stack align="center" gap="md" py="md">
            {qrCode && (
              <Card shadow="sm" padding="md" withBorder>
                <Image src={qrCode} alt="Verification QR Code" />
              </Card>
            )}
            <Text size="sm" c="dimmed" ta="center">
              Scan this QR code to begin verification
            </Text>
            <Button color="grape" onClick={handleQRScanned}>
              Scan QR Code
            </Button>
          </Stack>
        </Stepper.Step>
      )}

      <Stepper.Step
        label="Front of ID"
        description="Scan front of your ID"
        icon={<IconCamera size={18} />}
      >
        <Stack align="center" gap="md" py="md">
          {frontIdPreview ? (
            <Image src={frontIdPreview} alt="Front of ID" radius="md" maw={300} />
          ) : (
            <IconCamera size={48} color="var(--mantine-color-grape-6)" />
          )}
          <Text ta="center">
            Click to take a picture of the <strong>front</strong> of your ID
          </Text>
          <Alert color="pink" variant="light">Make sure the ID is clearly visible</Alert>
          <FileButton onChange={handleFrontIdUpload} accept="image/*">
            {(props) => (
              <Button {...props} color="grape">Take Photo of Front ID</Button>
            )}
          </FileButton>
        </Stack>
      </Stepper.Step>

      <Stepper.Step
        label="Back of ID"
        description="Scan back of your ID"
        icon={<IconCamera size={18} />}
      >
        <Stack align="center" gap="md" py="md">
          {backIdPreview ? (
            <Image src={backIdPreview} alt="Back of ID" radius="md" maw={300} />
          ) : (
            <IconCamera size={48} color="var(--mantine-color-grape-6)" />
          )}
          <Text ta="center">
            Click to take a picture of the <strong>back</strong> of your ID
          </Text>
          <Alert color="pink" variant="light">Make sure all text is visible</Alert>
          <FileButton onChange={handleBackIdUpload} accept="image/*">
            {(props) => (
              <Button {...props} color="grape">Take Photo of Back ID</Button>
            )}
          </FileButton>
        </Stack>
      </Stepper.Step>

      <Stepper.Step
        label="Selfie Verification"
        description="Take a selfie to match your face"
        icon={<IconCamera size={18} />}
      >
        <Stack align="center" gap="md" py="md">
          <IconCamera size={48} color="var(--mantine-color-grape-6)" />
          <Text ta="center">
            Take a <strong>selfie</strong> so we can verify it matches your ID
          </Text>
          <Alert color="pink" variant="light">Make sure your face is clearly visible</Alert>
          <Button color="grape" onClick={handleSelfieCapture}>Take Selfie</Button>
        </Stack>
      </Stepper.Step>

      <Stepper.Completed>
        <Stack align="center" gap="md" py="xl">
          <IconCheck size={48} color="var(--mantine-color-green-6)" />
          <Title order={2} ta="center">Verification Submitted!</Title>
          <Text ta="center" c="dimmed">
            Your identity documents have been submitted for review.
            We'll notify you once verification is complete.
          </Text>
          <Button color="grape" size="lg" onClick={handleClose} mt="md">
            Continue to Dashboard
          </Button>
        </Stack>
      </Stepper.Completed>
    </Stepper>
  );

  return (
    <Container size="sm" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="center">
          <Button
            component={Link}
            to="/become-princess"
            variant="subtle"
            leftSection={<IconArrowLeft size="1rem" />}
          >
            Back
          </Button>
        </Group>

        <Center>
          <Stack gap="md" align="center" maw={400}>
            <IconId size={48} color="var(--mantine-color-grape-6)" />
            <Title order={1} ta="center">Identity Verification</Title>
            <Text size="lg" ta="center" c="dimmed">
              Verify your identity to become a Pedi Princess
            </Text>
          </Stack>
        </Center>

        <Card shadow="md" padding="xl" radius="md" withBorder>
          {isMobile && step === 0 && !humanVerified ? (
            <Stack align="center" gap="xl" py="xl">
              <IconId size={64} color="var(--mantine-color-grape-6)" />
              <Title order={2} ta="center">Verify You're Human</Title>
              <Text size="lg" ta="center" c="dimmed" maw={300}>
                Please complete this verification to ensure you're a real person
              </Text>
              <Button color="grape" size="lg" onClick={handleHumanVerification}>
                I'm Human - Continue
              </Button>
            </Stack>
          ) : (
            renderStepper()
          )}
        </Card>

        {idCaptured && (
          <Notification color="green" title="ID Captured" onClose={() => setIdCaptured(false)}>
            Clear identification card front and back has been pictured
          </Notification>
        )}

        {selfieCaptured && (
          <Notification color="green" title="Selfie Captured" onClose={() => setSelfieCaptured(false)}>
            Clear selfie has been taken and uploaded
          </Notification>
        )}

        {ageVerified && (
          <Notification color="green" title="Age Verified" onClose={() => setAgeVerified(false)}>
            Age verification complete - 18 years or older
          </Notification>
        )}

        <Text size="sm" c="dimmed" ta="center">
          Your information is encrypted and secure.
          We only use it for verification purposes.
        </Text>
      </Stack>
    </Container>
  );
};

export default IdentityVerificationPage;
